// cSpell:ignore Afrekening afrekening betalen deze Huurcontract huurde huurt lopend maanden Provisies telt vanaf verrekend
// What the Provisies step says about a tenant (Lewis, 2026-10-16). Next to
// the name: nothing when the lease covers the whole settlement period, and a
// short sentence when the tenant moved in or out within it ("huurt vanaf
// 01/07/2026 · 6 van de 12 maanden") — "01/07/2026 → 31/12/2026 · 6
// maanden" was not understood. The "i" next to it lists the rest as label
// and value, one row per fact that applies (Lewis, 2026-10-17: the
// sentences read as blocks of text):
//
//   Huurcontract         01/07/2026 – lopend
//   In deze afrekening   01/07/2026 t/m 31/12/2026 · 6 maanden
//   Al verrekend         01/01/2026 t/m 30/06/2026 in "Afrekening H1"
//   Nog te betalen       01/11/2026 t/m 31/12/2026 (telt al mee)
//
// Months are the engine's (monthsBetween), so the step never says another
// number than the one the settlement bills.

import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { OPEN_ENDED_FROM } from "./departure.utils";
import { Interval, monthsBetween, SliceIntervals } from "./engine";
import { ProvisionSlice } from "./provisionHistory";
import {
  countedValue,
  describeProvisionPeriods,
  formatDay,
  formatMonthCount,
  IInfoRow,
  notYetDueRow,
} from "./provisionMonthsHint.utils";
import { ISettledSliceSource } from "./settledProvisions.utils";
import { ContractRef } from "./types";

type TLeaseDates = Pick<ContractRef, "startDate" | "endDate">;

export interface ITenantLeasePart {
  // The lease's days within the settlement period.
  from: string;
  to: string;
  months: number;
  // The months of the settlement period itself.
  periodMonths: number;
  // The tenant moved in, or out, within the settlement period.
  startsWithin: boolean;
  endsWithin: boolean;
}

export const tenantLeasePart = (params: {
  lease: TLeaseDates;
  period: Interval;
}): ITenantLeasePart => {
  const periodFrom = params.period.from.slice(0, 10);
  const periodTo = params.period.to.slice(0, 10);
  const leaseFrom = params.lease.startDate.slice(0, 10);
  const leaseTo = params.lease.endDate.slice(0, 10);
  const from = leaseFrom > periodFrom ? leaseFrom : periodFrom;
  const to = leaseTo < periodTo ? leaseTo : periodTo;
  return {
    from,
    to,
    months: monthsBetween(from, to),
    periodMonths: monthsBetween(periodFrom, periodTo),
    startsWithin: leaseFrom > periodFrom,
    endsWithin: leaseTo < periodTo,
  };
};

const isOpenEnded = (lease: TLeaseDates): boolean =>
  lease.endDate.slice(0, 10) >= OPEN_ENDED_FROM;

// A lease that ended before today is said in the past tense ("huurde tot"),
// one that still runs in the present ("huurt tot").
const NOTE_KEYS = {
  from: {
    present: "settlement.provisions.tenant_part.from",
    past: "settlement.provisions.tenant_part.from_past",
  },
  until: {
    present: "settlement.provisions.tenant_part.until",
    past: "settlement.provisions.tenant_part.until_past",
  },
  between: {
    present: "settlement.provisions.tenant_part.between",
    past: "settlement.provisions.tenant_part.between_past",
  },
};

// The short sentence next to the tenant's name, or undefined when the lease
// covers the whole settlement period: "huurt vanaf 01/07/2026 · 6 van de 12
// maanden", "huurde tot 30/06/2026 · 6 van de 12 maanden", "huurde van
// 01/03/2026 tot 30/09/2026 · 7 van de 12 maanden".
export const tenantPeriodNote = (params: {
  lease: TLeaseDates;
  period: Interval;
  today: string;
}): string | undefined => {
  const { lease, today } = params;
  const part = tenantLeasePart(params);
  if (!part.startsWithin && !part.endsWithin) return undefined;
  if (!(part.months > 0)) return undefined;
  const kind = (() => {
    if (part.startsWithin && part.endsWithin) return "between";
    return part.startsWithin ? "from" : "until";
  })();
  const tense = lease.endDate.slice(0, 10) < today ? "past" : "present";
  const when = getLocalizedText(NOTE_KEYS[kind][tense], {
    from: formatDay(part.from),
    to: formatDay(part.to),
  });
  const months = getLocalizedText(
    part.periodMonths === 1
      ? "settlement.provisions.tenant_part.months_of_one"
      : "settlement.provisions.tenant_part.months_of_many",
    {
      months: formatMonthCount(part.months),
      total: formatMonthCount(part.periodMonths),
    },
  );
  return `${when} · ${months}`;
};

const sameIntervals = (left: Interval[], right: Interval[]): boolean =>
  left.length === right.length &&
  left.every(
    (iv, index) => iv.from === right[index].from && iv.to === right[index].to,
  );

const SETTLED_KEYS: Record<ProvisionSlice | "all", string> = {
  all: "settlement.provisions.info.settled_in",
  common: "settlement.provisions.info.settled_in_common",
  private: "settlement.provisions.info.settled_in_private",
};

// One value per earlier settlement and its days: the kind of costs it
// settled the provision of, unless it settled every kind this settlement
// weighs for the lease.
const settledValues = (params: {
  slices: ProvisionSlice[];
  settledBySlice: Partial<Record<ProvisionSlice, ISettledSliceSource[]>>;
}): string[] => {
  const { slices, settledBySlice } = params;
  const groups: Array<ISettledSliceSource & { slices: ProvisionSlice[] }> = [];
  for (const slice of slices) {
    for (const source of settledBySlice[slice] ?? []) {
      const group = groups.find(
        candidate =>
          candidate.settlementName === source.settlementName &&
          sameIntervals(candidate.intervals, source.intervals),
      );
      if (group) {
        group.slices.push(slice);
      } else {
        groups.push({ ...source, slices: [slice] });
      }
    }
  }
  return groups.map(group =>
    getLocalizedText(
      SETTLED_KEYS[
        group.slices.length === slices.length ? "all" : group.slices[0]
      ],
      {
        periods: describeProvisionPeriods(group.intervals),
        settlement: group.settlementName,
      },
    ),
  );
};

// The rows behind the "i" on the tenant's row.
export const tenantPeriodRows = (params: {
  lease: TLeaseDates;
  // The slices of the provision this settlement weighs for the lease.
  slices: ProvisionSlice[];
  // The days this settlement counts: per slice, and of any slice.
  countedBySlice: SliceIntervals;
  counted: Interval[];
  // The days of each slice another settlement of this lease already
  // counted, and those an earlier lease of the same tenant settled.
  settledBySlice: Partial<Record<ProvisionSlice, ISettledSliceSource[]>>;
  earlierLease?: { intervals: Interval[]; settlementNames: string[] };
  today: string;
}): IInfoRow[] => {
  const { lease, slices, countedBySlice, counted, earlierLease } = params;
  const rows: IInfoRow[] = [
    {
      label: getLocalizedText("settlement.provisions.info.lease"),
      value: isOpenEnded(lease)
        ? getLocalizedText("settlement.provisions.info.lease_open", {
            from: formatDay(lease.startDate),
          })
        : `${formatDay(lease.startDate)} – ${formatDay(lease.endDate)}`,
    },
  ];
  // What counts: one period when every slice counts the same days, else
  // each provision line's own "i" says it.
  const perSlice = slices.map(slice => countedBySlice[slice] ?? []);
  const isUniform = perSlice.every(intervals =>
    sameIntervals(intervals, perSlice[0] ?? []),
  );
  if (counted.length > 0) {
    rows.push({
      label: getLocalizedText("settlement.provisions.info.counted"),
      value: isUniform
        ? countedValue(counted)
        : getLocalizedText("settlement.provisions.info.counted_per_line"),
    });
  }
  const settled = settledValues(params);
  if (earlierLease && earlierLease.intervals.length > 0) {
    settled.push(
      getLocalizedText("settlement.provisions.info.settled_earlier_lease", {
        periods: describeProvisionPeriods(earlierLease.intervals),
        settlements: earlierLease.settlementNames.join(", "),
      }),
    );
  }
  rows.push(
    ...settled.map(value => ({
      label: getLocalizedText("settlement.provisions.info.settled"),
      value,
    })),
  );
  const notDue = notYetDueRow({ intervals: counted, today: params.today });
  if (notDue) rows.push(notDue);
  return rows;
};
